import { slugifyHeadingId, uniqueHeadingId } from "./slugifyHeadingId";

export type TocHeadingItem = {
	id: string;
	level: number;
	text: string;
};

export const DEFAULT_TOC_LEVELS = [2, 3, 4];
export const DEFAULT_TOC_CONTENT_SELECTOR = '[data-layout-region="entry_body"]';

export function normalizeTocLevels(levels: number[] | undefined): number[] {
	const raw = levels?.length ? levels : DEFAULT_TOC_LEVELS;
	const uniq = [...new Set(raw.filter((n) => n >= 1 && n <= 6))].sort(
		(a, b) => a - b
	);
	return uniq.length > 0 ? uniq : DEFAULT_TOC_LEVELS;
}

export function tocHeadingsEqual(
	a: TocHeadingItem[],
	b: TocHeadingItem[]
): boolean {
	if (a.length !== b.length) {
		return false;
	}
	return a.every(
		(item, i) =>
			item.id === b[i]!.id &&
			item.level === b[i]!.level &&
			item.text === b[i]!.text
	);
}

const HEADING_TAG_RE = /<h([1-6])\b([^>]*)>([\s\S]*?)<\/h\1>/gi;
const SCRIPT_BLOCK_RE = /<script\b[^>]*>[\s\S]*?<\/script>/gi;
const STYLE_BLOCK_RE = /<style\b[^>]*>[\s\S]*?<\/style>/gi;
const HEADING_ID_ATTR_RE = /\bid\s*=\s*["']([^"']+)["']/i;

/** Parse h1–h6 from HTML/JSX markup (custom_code source, staticHtml, etc.). */
export function extractHeadingsFromHtmlMarkup(
	html: string,
	levels: number[],
	used: Set<string>
): TocHeadingItem[] {
	const cleaned = html.replace(SCRIPT_BLOCK_RE, "").replace(STYLE_BLOCK_RE, "");
	const items: TocHeadingItem[] = [];
	for (const match of cleaned.matchAll(HEADING_TAG_RE)) {
		const level = Number.parseInt(match[1] ?? "", 10);
		if (!(Number.isFinite(level) && levels.includes(level))) {
			continue;
		}
		const attrs = match[2] ?? "";
		const inner = match[3] ?? "";
		const text = inner.replace(/<[^>]+>/g, "").trim();
		if (!text) {
			continue;
		}
		const existingId = attrs.match(HEADING_ID_ATTR_RE)?.[1]?.trim() ?? "";
		const id = existingId
			? uniqueHeadingId(existingId, used)
			: uniqueHeadingId(slugifyHeadingId(text), used);
		items.push({ id, level, text });
	}
	return items;
}

function collectTocHeadingsFromDomRoot(
	root: Element,
	levels: number[],
	excludeSelector?: string,
	used?: Set<string>
): TocHeadingItem[] {
	const selector = levels.map((l) => `h${l}`).join(",");
	const nodes = root.querySelectorAll(selector);
	const excludeRoots = excludeSelector?.trim()
		? [...root.querySelectorAll(excludeSelector.trim())]
		: [];
	const idUsed = used ?? new Set<string>();
	const items: TocHeadingItem[] = [];

	for (const node of nodes) {
		if (!(node instanceof HTMLHeadingElement)) {
			continue;
		}
		if (
			excludeRoots.some(
				(excluded) => excluded === node || excluded.contains(node)
			)
		) {
			continue;
		}
		const text = node.textContent?.trim() ?? "";
		if (!text) {
			continue;
		}
		const level = Number.parseInt(node.tagName.slice(1), 10);
		if (!levels.includes(level)) {
			continue;
		}
		let id = node.id?.trim() ?? "";
		if (id) {
			if (idUsed.has(id)) {
				id = uniqueHeadingId(id, idUsed);
				node.id = id;
			} else {
				idUsed.add(id);
			}
		} else {
			id = uniqueHeadingId(slugifyHeadingId(text), idUsed);
			node.id = id;
		}
		items.push({ id, level, text });
	}

	return items;
}

export function collectTocHeadingsFromDom(
	root: Element,
	levels: number[],
	excludeSelector?: string
): TocHeadingItem[] {
	const used = new Set<string>();
	const items = collectTocHeadingsFromDomRoot(
		root,
		levels,
		excludeSelector,
		used
	);
	for (const iframe of root.querySelectorAll("iframe")) {
		try {
			const body = iframe.contentDocument?.body;
			if (body) {
				items.push(
					...collectTocHeadingsFromDomRoot(body, levels, excludeSelector, used)
				);
			}
		} catch {
			// cross-origin or inaccessible iframe — skip
		}
	}
	return items;
}

type BuilderHeadingLike = {
	content?: string;
	tag?: string;
	type?: string;
	children?: BuilderHeadingLike[];
};

/** Walk a flat builder element list for heading nodes (editor fallback). */
export function collectTocHeadingsFromBuilderElements(
	elements: BuilderHeadingLike[] | undefined,
	levels: number[]
): TocHeadingItem[] {
	if (!elements?.length) {
		return [];
	}
	const used = new Set<string>();
	const items: TocHeadingItem[] = [];

	const walk = (nodes: BuilderHeadingLike[]) => {
		for (const node of nodes) {
			if (node.type === "heading") {
				const tag = (node.tag ?? "h2").toLowerCase();
				const level = Number.parseInt(tag.replace("h", ""), 10);
				const text = (node.content ?? "").replace(/<[^>]+>/g, "").trim();
				if (text && levels.includes(level)) {
					const id = uniqueHeadingId(slugifyHeadingId(text), used);
					items.push({ id, level, text });
				}
			}
			const children = (node as { children?: BuilderHeadingLike[] }).children;
			if (Array.isArray(children) && children.length > 0) {
				walk(children);
			}
		}
	};

	walk(elements);
	return items;
}
